import NotificationManager from '../utils/NotificationManager';
import { getCookie } from '../utils/CookieManager';

const API_BASE_URL = process.env.REACT_APP_FIREBASE_API_URL;

// Fallback handling without exposing URL
if (!API_BASE_URL) {
  console.warn('REACT_APP_FIREBASE_API_URL not set, using fallback configuration');
}

// Use a function to get the URL to avoid exposing it in the code
const getApiUrl = () => API_BASE_URL || null;

// Constants for Firebase paths
const PATHS = {
  PENDING_LINKS: '/neuranet/pending_links',
  MINECRAFT_LINKS: '/neuranet/minecraft/links',
  PLAYERS: '/players'
};

class PlayerService {
  isAuthenticated() {
    // Check both possible cookie keys
    const discordId = getCookie('discord_id') || getCookie('discordUID');
    
    if (!discordId) {
      return Promise.resolve(false);
    }

    // First check AccountStore for existing profile
    const accountStore = require('../components/account/AccountStore').default;
    const currentProfile = accountStore.getProfile();
    
    if (currentProfile?.data) {
      return Promise.resolve(true);
    }
    
    // If no valid profile in AccountStore, try to get from Firebase
    return this.getPlayerData(discordId)
      .then(response => {
        if (response?.data) {
          // Update AccountStore with the valid profile
          accountStore.setProfile(response);
          return true;
        }
        return false;
      })
      .catch(err => {
        console.error('Firebase request failed:', err);
        return false;
      });
  }

  async getAllPlayers() {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}.json`);
      if (!response.ok) {
        throw new Error('Failed to fetch players data');
      }
      const players = await response.json();
      
      // Convert to array and sort by qubits
      const playersArray = Object.entries(players).map(([id, data]) => ({
        id,
        ...data
      }));
      // Map ranks to numeric values for sorting
      const getRankValue = (rank) => {
        if (!rank || rank === '0') return Number.MAX_SAFE_INTEGER;  // No rank is lowest
        const numericRank = parseInt(rank);
        return isNaN(numericRank) ? Number.MAX_SAFE_INTEGER : numericRank;
      };

      // Sort by rank first (lower ranks first as they're better), then by qubits
      playersArray.sort((a, b) => {
        const rankA = getRankValue(a.rank);
        const rankB = getRankValue(b.rank);
        
        // Primary sort by rank (lower is better)
        if (rankA !== rankB) {
          return rankA - rankB;  // Lower rank first
        }
        
        // Secondary sort by qubits (only if ranks are equal)
        return (b.qubits || 0) - (a.qubits || 0);
      });
      playersArray.sort((a, b) => (b.qubits || 0) - (a.qubits || 0));
      
      return playersArray;
    } catch (error) {
      console.error('Error fetching all players:', error);
      return [];
    }
  }

  async findPlayerByUsername(username) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Get all players
      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}.json`);
      if (!response.ok) {
        throw new Error('Failed to fetch players data');
      }
      const players = await response.json();
      
      // Find player by username
      // Normalize the search username: lowercase and remove spaces
      const normalizedSearch = username.toLowerCase().replace(/\s+/g, '');
      
      // Find player by normalized username
      const playerId = Object.keys(players).find(id => {
        const storedUsername = players[id].discordUsername;
        if (!storedUsername) return false;
        // Normalize stored username same way
        const normalizedStored = storedUsername.toLowerCase().replace(/\s+/g, '');
        return normalizedStored === normalizedSearch;
      });
      
      if (playerId) {
        return {
          playerId,
          data: players[playerId],
          timestamp: new Date().toISOString()
        };
      }
      return null;
    } catch (error) {
      console.error('Error finding player by username:', error);
      return null;
    }
  }

  async getPlayerData(discordId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Directly use the Discord ID to get player data
      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}.json`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }
      
      const data = await response.json();
   
      // Check if we got actual data back
      if (data === null) {
        return {
          playerId: discordId,
          data: null,
          timestamp: new Date().toISOString()
        };
      }
      
      const result = {
        playerId: discordId,
        data: data,
        timestamp: new Date().toISOString()
      };
      
      return result;
    } catch (error) {
      console.error('Error fetching player data:', {
        error,
        message: error.message,
        stack: error.stack
      });
      return {
        playerId: discordId,
        data: null,
        timestamp: new Date().toISOString()
      };
    }
  }

  async updatePlayerData(discordId, data, options = { increment: true }) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      let qubitAmountAdded = 0; // Track the amount added for global tracking

      // If updating qubits and increment is true, check rate limit and add to current value
      if ('qubits' in data && options.increment) {
        const currentData = await this.getPlayerData(discordId);
        if (currentData && currentData.data) {
          // Get current UTC timestamp
          const nowUTC = Date.now();

          // Get or initialize the 24h window data
          const windowData = currentData.data.qubitWindow || {
            startTime: nowUTC,
            totalQubits: 0
          };

          // Check if window has expired (24h = 86400000ms)
          const windowAge = nowUTC - windowData.startTime;
          if (windowAge >= 86400000) {
            // Window expired, start fresh
            windowData.startTime = nowUTC;
            windowData.totalQubits = 0;
          }

          // Check if new submission would exceed limit
          if (windowData.totalQubits + Number(data.qubits) > 20000) {
            // Calculate time until window expires
            const timeUntilReset = 86400000 - windowAge;
            const hoursLeft = Math.floor(timeUntilReset / 3600000);
            const minutesLeft = Math.floor((timeUntilReset % 3600000) / 60000);

            // Calculate exact reset time in user's timezone
            const resetTime = new Date(windowData.startTime + 86400000);
            const resetTimeLocal = resetTime.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });

            const message = `Qubit submission limit exceeded. You have submitted ${windowData.totalQubits} qubits in this 24-hour window. Your window resets at ${resetTimeLocal} local time (${hoursLeft}h ${minutesLeft}m from now).`;
            NotificationManager.notify(message);
            throw new Error(message);
          }

          const currentQubits = Number(currentData.data.qubits || 0);
          qubitAmountAdded = Number(data.qubits); // Store the increment amount

          // Update data with new qubits and window info
          data = {
            ...data,
            qubits: currentQubits + Number(data.qubits),
            qubitWindow: {
              startTime: windowData.startTime,
              totalQubits: windowData.totalQubits + Number(data.qubits)
            }
          };
        }
      }

      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}.json`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          lastUpdated: new Date().toISOString()
        })
      });

      if (!response.ok) {
        console.error('Failed to update player data:', await response.text());
        return false;
      }

      // Log to global qubit tracking if qubits were added
      if (qubitAmountAdded > 0) {
        await this.logQubitTransaction(discordId, qubitAmountAdded);
      }

      return true;
    } catch (error) {
      console.error('Error updating player data:', error);
      return false;
    }
  }

  async updateAchievement(discordId, eventId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}/achievements/${eventId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(true)
      });
      return response.ok;
    } catch (error) {
      console.error('Error updating achievement:', error);
      return false;
    }
  }

  async findOrCreatePlayerByDiscord({ discordId, discordUsername, avatar, referralCode }) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // First try to get existing player
      const existingPlayer = await this.getPlayerData(discordId);
      if (existingPlayer && existingPlayer.data) {
        return existingPlayer;
      }

      // Generate a unique referral code for the new player
      const uniqueCode = await this.generateReferralCode(discordUsername);

      // If player doesn't exist, initialize them
      const initialData = {
        achievements: {
          event_001: true
        },
        discordUsername,
        lastUpdated: new Date().toISOString(),
        qubits: 0,
        rank: '0',
        isAdmin: false, // Default to false, must be manually set to true in Firebase
        referral: {
          code: uniqueCode,
          count: 0,
          referredUsers: [],
          totalEarned: 0,
          lastUpdated: new Date().toISOString()
        }
      };

      // Handle referral if code provided
      if (referralCode) {
        console.log('🔗 Processing referral with code:', referralCode);
        const referrer = await this.findPlayerByReferralCode(referralCode);
        if (referrer) {
          console.log('✓ Found referrer:', referrer.playerId);
          // Add referral data to new player
          initialData.referredBy = referrer.playerId;
          initialData.referredAt = new Date().toISOString();

          // Process reward for referrer
          const rewardSuccess = await this.processReferralReward(referrer.playerId, discordId);
          console.log('Referral reward processed:', rewardSuccess);

          if (!rewardSuccess) {
            console.error('✗ FAILED TO PROCESS REFERRAL REWARD');
            // Log failed referral for manual review
            await this.logFailedReferral({
              referralCode,
              referrerId: referrer.playerId,
              newUserId: discordId,
              newUsername: discordUsername,
              timestamp: new Date().toISOString(),
              reason: 'Reward processing failed'
            });
          }
        } else {
          console.error('✗ NO REFERRER FOUND FOR CODE:', referralCode);
          // Log failed referral for manual review
          await this.logFailedReferral({
            referralCode,
            newUserId: discordId,
            newUsername: discordUsername,
            timestamp: new Date().toISOString(),
            reason: 'Referrer not found in database'
          });
        }
      }

      // Store the player data using Discord ID
      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(initialData)
      });

      if (!response.ok) {
        throw new Error('Failed to create player');
      }

      // Return the newly created player data
      return {
        playerId: discordId,
        data: initialData,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error in findOrCreatePlayerByDiscord:', error);
      throw error;
    }
  }

  async generateReferralCode() {
    // Generate 8 character alphanumeric code
    const generateCode = () => {
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    };

    // Try to generate a unique code
    try {
      let code = generateCode();
      let attempts = 0;
      const maxAttempts = 5;

      // Keep trying until we find a unique code or hit max attempts
      while (attempts < maxAttempts) {
        const existing = await this.findPlayerByReferralCode(code);
        if (!existing) {
          return code;
        }
        code = generateCode();
        attempts++;
      }
      
      // If we hit max attempts, add timestamp to ensure uniqueness
      return generateCode() + Date.now().toString(36).slice(-2);
    } catch (error) {
      console.error('Error generating referral code:', error);
      throw error;
    }
  }

  async findPlayerByReferralCode(code) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Normalize code to uppercase to handle case sensitivity
      const normalizedCode = code.toUpperCase().trim();

      // Get all players
      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}.json`);
      if (!response.ok) {
        throw new Error('Failed to fetch players data');
      }

      const players = await response.json();

      if (!players) {
        console.error('No players found in database');
        return null;
      }

      // Find player with matching referral code (case-insensitive)
      for (const [playerId, playerData] of Object.entries(players)) {
        if (playerData.referral?.code &&
            playerData.referral.code.toUpperCase() === normalizedCode) {
          console.log('✓ Found referrer:', playerId, 'for code:', code);
          return {
            playerId,
            data: playerData,
            timestamp: new Date().toISOString()
          };
        }
      }

      console.error('✗ No player found with referral code:', code);
      console.error('Searched through', Object.keys(players).length, 'players');
      return null;
    } catch (error) {
      console.error('Error finding player by referral code:', error);
      return null;
    }
  }

  async processReferralReward(referrerId, referredId) {
    try {
      console.log('Processing referral reward:', { referrerId, referredId });
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Get referrer's data
      const referrer = await this.getPlayerData(referrerId);
      if (!referrer?.data) {
        throw new Error('Invalid referrer data');
      }

      // Define tier rewards and multipliers
      const TIER_REWARDS = {
        direct: 5000,     // Direct referral reward
        level2: 2500,      // 2nd level (referral of referral)
        level3: 1000,      // 3rd level
      };

      // Get current referral data
      const referral = referrer.data.referral || {
        code: '',
        count: 0,
        lastUpdated: new Date().toISOString(),
        referredUsers: [],
        totalEarned: 0,
        referralChain: {} // Track full referral tree
      };

      // Calculate rewards
      let totalReward = TIER_REWARDS.direct;
      console.log('Base reward:', TIER_REWARDS.direct);

      // Check if this referral has a referrer (for level 2 reward)
      if (referrer.data.referredBy) {
        const level2Referrer = await this.getPlayerData(referrer.data.referredBy);
        if (level2Referrer?.data) {
          // Process level 2 reward
          await this.updatePlayerData(level2Referrer.playerId, {
            qubits: (level2Referrer.data.qubits || 0) + TIER_REWARDS.level2,
            referral: {
              ...level2Referrer.data.referral,
              totalEarned: (level2Referrer.data.referral?.totalEarned || 0) + TIER_REWARDS.level2
            }
          });
          console.log('Level 2 reward processed:', TIER_REWARDS.level2);

          // Check for level 3 reward
          if (level2Referrer.data.referredBy) {
            const level3Referrer = await this.getPlayerData(level2Referrer.data.referredBy);
            if (level3Referrer?.data) {
              // Process level 3 reward
              await this.updatePlayerData(level3Referrer.playerId, {
                qubits: (level3Referrer.data.qubits || 0) + TIER_REWARDS.level3,
                referral: {
                  ...level3Referrer.data.referral,
                  totalEarned: (level3Referrer.data.referral?.totalEarned || 0) + TIER_REWARDS.level3
                }
              });
              console.log('Level 3 reward processed:', TIER_REWARDS.level3);
            }
          }
        }
      }

      // Calculate new qubits total
      const currentQubits = referrer.data.qubits || 0;
      const updatedQubits = currentQubits + totalReward;

      console.log('Current qubits:', currentQubits);
      console.log('Adding total reward:', totalReward);
      console.log('New total:', updatedQubits);

      // Update referral chain
      const referralChain = {
        ...referral.referralChain,
        [referredId]: {
          joinedAt: new Date().toISOString(),
          reward: totalReward
        }
      };

      // Ensure referredUsers array exists
      const currentReferredUsers = referral.referredUsers || [];

      // Preserve all existing data while updating necessary fields
      const referrerUpdates = {
        ...referrer.data, // Keep all existing data
        qubits: updatedQubits, // Update qubits
        referral: {
          ...referral,
          count: (referral.count || 0) + 1,
          lastUpdated: new Date().toISOString(),
          referredUsers: [...currentReferredUsers, referredId],
          totalEarned: (referral.totalEarned || 0) + totalReward,
          referralChain,
          tiers: {
            ...referral.tiers,
            direct: (referral.tiers?.direct || 0) + 1,
            totalDirect: (referral.tiers?.totalDirect || 0) + TIER_REWARDS.direct,
            level2Earnings: (referral.tiers?.level2Earnings || 0),
            level3Earnings: (referral.tiers?.level3Earnings || 0)
          }
        },
        lastUpdated: new Date().toISOString()
      };

      console.log('Adding new referred user ID:', referredId);
      console.log('Current qubits:', currentQubits);
      console.log('Adding reward:', totalReward);
      console.log('New total:', updatedQubits);
      console.log('Full update payload:', referrerUpdates);

      // Use PUT to ensure complete data update
      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${referrerId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(referrerUpdates)
      });

      if (!response.ok) {
        console.error('Failed to update referrer data:', await response.text());
        return false;
      }

      console.log('Referral reward processed successfully');
      return true;
    } catch (error) {
      console.error('Error processing referral reward:', error);
      return false;
    }
  }

  async logFailedReferral(failureData) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        console.error('Cannot log failed referral - API URL not configured');
        return;
      }

      const timestamp = Date.now();
      const failureId = `${timestamp}-${Math.random().toString(36).substring(2, 8)}`;

      await fetch(`${apiUrl}/referrals/failed/${failureId}.json`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(failureData)
      });

      console.log('Failed referral logged for manual review:', failureId);
    } catch (error) {
      console.error('Error logging failed referral:', error);
    }
  }

  async logQubitTransaction(discordId, amount) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 8);
      const transactionId = `${timestamp}-${randomId}`;

      // 2. Get current running total FIRST (before adding transaction)
      const totalResponse = await fetch(`${apiUrl}/qubits/runningTotal.json`);
      let currentTotal = 0;
      if (totalResponse.ok) {
        const total = await totalResponse.json();
        currentTotal = Number(total) || 0;
      }

      // Calculate new running total
      const newTotal = currentTotal + amount;

      // 1. Add transaction to /qubits/transactions/ with running total
      const transactionResponse = await fetch(`${apiUrl}/qubits/transactions/${transactionId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: discordId,
          amount: amount,
          timestamp: timestamp,
          runningTotal: newTotal  // Store the running total at this point!
        })
      });

      if (!transactionResponse.ok) {
        console.error('Failed to log qubit transaction:', await transactionResponse.text());
        return false;
      }

      // 3. Update the running total in Firebase
      const updateTotalResponse = await fetch(`${apiUrl}/qubits/runningTotal.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTotal)
      });

      if (!updateTotalResponse.ok) {
        console.error('Failed to update running total:', await updateTotalResponse.text());
        return false;
      }

      console.log(`Logged qubit transaction: ${amount} qubits for user ${discordId}. New total: ${newTotal}`);
      return true;
    } catch (error) {
      console.error('Error logging qubit transaction:', error);
      // Don't throw - this shouldn't break the main qubit earning flow
      return false;
    }
  }

  async isModuleEnabled(moduleName) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Check if module is enabled in Firebase
      const response = await fetch(`${apiUrl}/features/${moduleName}.json`);
      if (!response.ok) {
        throw new Error('Failed to check module status');
      }

      const isEnabled = await response.json();
      console.log(`Module ${moduleName} enabled:`, isEnabled);
      return isEnabled === true;
    } catch (error) {
      console.error(`Error checking module ${moduleName}:`, error);
      return false;
    }
  }

  async linkMinecraftAccount(discordId, minecraftUuid) {
    try {
      console.log('Linking Minecraft account:', { discordId, minecraftUuid });
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Check if Minecraft UUID exists and is available for linking
      const existingLink = await this.getMinecraftLink(minecraftUuid);
      console.log('Checking existing link:', { minecraftUuid, existingLink });
      
      // Only proceed if:
      // 1. No existing link OR
      // 2. Existing link is null/"null"/empty OR
      // 3. Existing link matches the UUID (pending state) OR
      // 4. Existing link matches the Discord ID (already linked to this user)
      if (existingLink &&
          existingLink !== "null" &&
          existingLink !== "" &&
          existingLink !== minecraftUuid &&
          existingLink !== discordId) {
        console.error('Minecraft UUID already linked to different Discord ID:', existingLink);
        return false;
      }

      // Check if Discord ID already has a different Minecraft account
      const playerData = await this.getPlayerData(discordId);
      if (playerData?.data?.minecraft?.uuid &&
          playerData.data.minecraft.uuid !== minecraftUuid) {
        console.error('Discord ID already has different Minecraft account:', playerData.data.minecraft.uuid);
        return false;
      }

      // Perform both updates in sequence, rollback if either fails
      try {
        console.log('Starting Firebase updates for linking...', {
          discordId,
          minecraftUuid,
          apiUrl,
          playerPath: `${apiUrl}${PATHS.PLAYERS}/${discordId}/minecraft.json`,
          linkPath: `${apiUrl}${PATHS.MINECRAFT_LINKS}/${minecraftUuid}.json`
        });

        // 1. Store Discord ID to Minecraft UUID mapping in player data
        const updatePlayerResponse = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}/minecraft.json`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            uuid: minecraftUuid,
            linkedAt: new Date().toISOString()
          })
        });

        const playerResponseText = await updatePlayerResponse.text();
        console.log('Player update response:', {
          status: updatePlayerResponse.status,
          ok: updatePlayerResponse.ok,
          response: playerResponseText
        });

        if (!updatePlayerResponse.ok) {
          throw new Error(`Failed to update player data: Status ${updatePlayerResponse.status}, Response: ${playerResponseText}`);
        }

        // Verify player update
        const verifyPlayerResponse = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}/minecraft.json`);
        const verifyPlayerData = await verifyPlayerResponse.json();
        console.log('Verify player update:', verifyPlayerData);
        
        if (!verifyPlayerData || verifyPlayerData.uuid !== minecraftUuid) {
          throw new Error(`Player update verification failed: ${JSON.stringify(verifyPlayerData)}`);
        }

        // 2. Store Discord ID in minecraft_links
        const updateMinecraftLinkResponse = await fetch(`${apiUrl}${PATHS.MINECRAFT_LINKS}/${minecraftUuid}.json`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(discordId)
        });

        const linkResponseText = await updateMinecraftLinkResponse.text();
        console.log('Minecraft link update response:', {
          status: updateMinecraftLinkResponse.status,
          ok: updateMinecraftLinkResponse.ok,
          response: linkResponseText
        });

        if (!updateMinecraftLinkResponse.ok) {
          // Rollback player data update
          console.log('Rolling back player data update due to link update failure');
          const rollbackResponse = await fetch(`${apiUrl}${PATHS.PLAYERS}/${discordId}/minecraft.json`, {
            method: 'DELETE'
          });
          console.log('Rollback response:', {
            status: rollbackResponse.status,
            ok: rollbackResponse.ok
          });
          
          throw new Error(`Failed to update Minecraft links: Status ${updateMinecraftLinkResponse.status}, Response: ${linkResponseText}`);
        }

        // Verify minecraft link update
        const verifyLinkResponse = await fetch(`${apiUrl}${PATHS.MINECRAFT_LINKS}/${minecraftUuid}.json`);
        const verifyLinkData = await verifyLinkResponse.json();
        console.log('Verify minecraft link update:', verifyLinkData);
        
        if (verifyLinkData !== discordId) {
          throw new Error(`Minecraft link verification failed: ${JSON.stringify(verifyLinkData)}`);
        }

        console.log('Successfully completed both updates with verification');
        return true;

      } catch (error) {
        console.error('Error during atomic update:', error);
        return false;
      }
    } catch (error) {
      console.error('Error linking Minecraft account:', error);
      return false;
    }
  }

  async findPendingMinecraftLink() {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}${PATHS.MINECRAFT_LINKS}.json`);
      if (!response.ok) {
        throw new Error('Failed to fetch minecraft links');
      }

      const minecraftLinks = await response.json();
      if (!minecraftLinks) {
        console.log('No minecraft links found');
        return null;
      }

      // Find entries where the key is a UUID and value indicates it's pending
      const entry = Object.entries(minecraftLinks).find(([key, value]) => {
        // Check if the key is a UUID format (8-4-4-4-12)
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        // Consider it pending if:
        // 1. The key matches UUID pattern AND
        // 2. The value is either null, "null", empty string, or matches the UUID (indicating pending)
        return uuidPattern.test(key) && (!value || value === "null" || value === "" || value === key);
      });

      if (!entry) {
        console.log('No pending minecraft link found');
        return null;
      }

      const [minecraftUuid, _] = entry;
      console.log('Found pending minecraft link:', minecraftUuid);
      return minecraftUuid;
    } catch (error) {
      console.error('Error finding pending Minecraft link:', error);
      return null;
    }
  }

  async getMinecraftLink(minecraftUuid) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}${PATHS.MINECRAFT_LINKS}/${minecraftUuid}.json`);
      if (!response.ok) {
        throw new Error('Failed to fetch Minecraft link');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting Minecraft link:', error);
      return null;
    }
  }

  // IDCU-OS Visitor Check

  /**
   * Checks if a player has visited IDCU-OS by looking up their ID in the VISITORS list
   * This is required before accessing the Defragger
   */
  async checkIdcuOsVisitor(playerId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}/IDCU-OS/VISITORS/${playerId}.json`);
      if (!response.ok) {
        return false;
      }

      const visitorData = await response.json();
      console.log('IDCU-OS visitor check for player', playerId, ':', visitorData);

      // If the player exists in the VISITORS list, they have visited
      return visitorData !== null;
    } catch (error) {
      console.error('Error checking IDCU-OS visitor status:', error);
      return false;
    }
  }

  // Fragment Redemption Methods

  /**
   * Fetches pending fragments for a player from /neuranet/pending_fragments/{playerId}
   * The playerId IS the key in pending_fragments collection
   * Fragment structure at /neuranet/pending_fragments/{playerId}:
   *   { key: "sel_a_4529", unlocks: "selenix_journal_fragment_01", generated: "..." }
   */
  async getPendingFragments(playerId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}/neuranet/pending_fragments/${playerId}.json`);
      if (!response.ok) {
        return [];
      }

      const fragmentData = await response.json();
      console.log('Fetched pending fragment data for player', playerId, ':', fragmentData);

      if (!fragmentData) return [];

      // The data is stored directly at /pending_fragments/{playerId} as a single object
      // { key: "sel_a_4529", unlocks: "selenix_journal_fragment_01", generated: "...", msg: "optional" }
      // Return as array with the playerId as fragmentId for deletion reference
      return [{
        fragmentId: playerId,
        key: fragmentData.key,
        unlocks: fragmentData.unlocks,
        generated: fragmentData.generated,
        msg: fragmentData.msg || null
      }];
    } catch (error) {
      console.error('Error fetching pending fragments:', error);
      return [];
    }
  }

  // Legacy method - kept for backwards compatibility
  async getPendingKeys(playerId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}/neuranet/pendingKeys/${playerId}.json`);
      if (!response.ok) {
        return [];
      }

      const keysData = await response.json();
      if (!keysData) return [];

      // Convert object to array of key objects
      return Object.entries(keysData).map(([keyId, data]) => ({
        keyId,
        key: data.key,
        fileId: data.fileId,
        filePath: data.filePath || data.fileId,
        createdAt: data.createdAt
      }));
    } catch (error) {
      console.error('Error fetching pending keys:', error);
      return [];
    }
  }

  // File unlock order - defines prerequisites for each file
  // Based on Chapter 2 Flow documentation
  static FILE_UNLOCK_ORDER = {
    // Phase 2: Selenix Journal Chain
    'selenix_journal_fragment_01': {
      order: 1,
      prerequisites: [], // First unlock, no prerequisites
    },
    'selenix_journal_fragment_02': {
      order: 2,
      prerequisites: ['selenix_journal_fragment_01'],
    },
    'selenix_journal_fragment_03': {
      order: 3,
      prerequisites: ['selenix_journal_fragment_02'],
    },
    'selenix_personnel_file': {
      order: 3, // Unlocks together with fragment 03
      prerequisites: ['selenix_journal_fragment_02'],
    },
    // Phase 3: ROM Entity Investigation
    'rom_entity_dossier': {
      order: 4,
      prerequisites: ['selenix_journal_fragment_03'],
    },
    'classified_correspondence': {
      order: 5,
      prerequisites: ['rom_entity_dossier'],
    },
    'mf_entity_dossier': {
      order: 6,
      prerequisites: ['classified_correspondence'],
      requiresFragmentCount: 3, // Must have 3+ fragments redeemed
    },
    // Phase 4: Leadership Access & X Entity
    'solis_personnel_file': {
      order: 7,
      prerequisites: ['mf_entity_dossier'],
    },
    'x_entity_dossier': {
      order: 8,
      prerequisites: ['solis_personnel_file'],
    },
    // Phase 5: Finale
    'incident_9172': {
      order: 9,
      prerequisites: ['x_entity_dossier'],
    },
  };

  /**
   * Validates if a file can be unlocked based on prerequisites
   * Returns { valid: boolean, reason: string }
   */
  validateUnlockPrerequisites(fileToUnlock, unlockedFiles, fragmentsRedeemed) {
    const fileConfig = PlayerService.FILE_UNLOCK_ORDER[fileToUnlock];

    // If file not in order config, allow unlock (backwards compatibility)
    if (!fileConfig) {
      console.log(`File ${fileToUnlock} not in unlock order config, allowing unlock`);
      return { valid: true, reason: null };
    }

    // Check fragment count requirement (for mf_entity_dossier)
    if (fileConfig.requiresFragmentCount && fragmentsRedeemed < fileConfig.requiresFragmentCount) {
      return {
        valid: false,
        reason: 'INSUFFICIENT CLEARANCE LEVEL'
      };
    }

    // Check all prerequisites are unlocked
    for (const prereq of fileConfig.prerequisites) {
      if (!unlockedFiles[prereq]) {
        return {
          valid: false,
          reason: 'ORDER OUT OF OPERATIONS: SEQUENTIAL UNLOCK NOT SATISFIED'
        };
      }
    }

    return { valid: true, reason: null };
  }

  /**
   * Redeems a fragment key for a player
   * 1. Validates the key exists in pending_fragments
   * 2. Checks prerequisites are met (sequential unlock order)
   * 3. Sets unlockedFiles/{unlocks} to true in player data
   * 4. Increments fragmentsRedeemed count
   * 5. Removes the fragment from pending_fragments
   * 6. Logs the redemption
   */
  async redeemFragment(playerId, fileToUnlock, fragmentKey, fragmentId) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Get current player data
      const playerData = await this.getPlayerData(playerId);
      if (!playerData?.data) {
        throw new Error('Player data not found');
      }

      const currentFragments = playerData.data.fragmentsRedeemed || 0;
      const unlockedFiles = playerData.data.unlockedFiles || {};

      // Validate prerequisites before allowing unlock
      const validation = this.validateUnlockPrerequisites(fileToUnlock, unlockedFiles, currentFragments);
      if (!validation.valid) {
        throw new Error(validation.reason);
      }

      // Update player data: set the specific file to true in unlockedFiles
      const updates = {
        fragmentsRedeemed: currentFragments + 1,
        [`unlockedFiles/${fileToUnlock}`]: true,
        lastFragmentRedemption: new Date().toISOString()
      };

      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${playerId}.json`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update player data');
      }

      // Remove the used fragment from pending_fragments
      // The fragment is stored at /neuranet/pending_fragments/{playerId} directly
      if (fragmentId) {
        await fetch(`${apiUrl}/neuranet/pending_fragments/${fragmentId}.json`, {
          method: 'DELETE'
        });
      }

      // Log the redemption
      await this.logFragmentRedemption(playerId, fileToUnlock, fragmentKey);

      return true;
    } catch (error) {
      console.error('Error redeeming fragment:', error);
      throw error;
    }
  }

  async logFragmentRedemption(playerId, fileId, fragmentKey) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const timestamp = Date.now();
      const redemptionId = `${timestamp}-${Math.random().toString(36).substring(2, 8)}`;

      await fetch(`${apiUrl}/neuranet/fragmentRedemptions/${redemptionId}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId,
          fileId,
          fragmentKey,
          timestamp,
          redeemedAt: new Date().toISOString()
        })
      });

      console.log('Fragment redemption logged:', redemptionId);
      return true;
    } catch (error) {
      console.error('Error logging fragment redemption:', error);
      return false;
    }
  }

  /**
   * Check if a key matches a folder password in /neuranet/fragments/{key}
   * Returns the folder password data if found, null otherwise
   */
  async getFolderPassword(key) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      const response = await fetch(`${apiUrl}/neuranet/fragments/${key}.json`);
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data; // { fragmentsRequired, unlocks }
    } catch (error) {
      console.error('Error fetching folder password:', error);
      return null;
    }
  }

  /**
   * Redeem a folder password key
   * Checks fragmentsRequired against player's fragmentsRedeemed
   * Unlocks all files in the unlocks array/string
   */
  async redeemFolderPassword(playerId, folderPasswordKey, folderPasswordData) {
    try {
      const apiUrl = getApiUrl();
      if (!apiUrl) {
        throw new Error('API URL not configured');
      }

      // Get current player data
      const playerData = await this.getPlayerData(playerId);
      if (!playerData?.data) {
        throw new Error('Player data not found');
      }

      const currentFragments = playerData.data.fragmentsRedeemed || 0;
      const unlockedFiles = playerData.data.unlockedFiles || {};

      // Check if player has enough fragments
      if (currentFragments < folderPasswordData.fragmentsRequired) {
        throw new Error(`INSUFFICIENT CLEARANCE - ${folderPasswordData.fragmentsRequired} FRAGMENTS REQUIRED`);
      }

      // Normalize unlocks to array
      let filesToUnlock = folderPasswordData.unlocks;
      if (!Array.isArray(filesToUnlock)) {
        filesToUnlock = [filesToUnlock];
      }

      // Check if any files are already unlocked
      const alreadyUnlocked = filesToUnlock.filter(file => unlockedFiles[file.trim()] === true);
      if (alreadyUnlocked.length === filesToUnlock.length) {
        throw new Error('FILES ALREADY UNLOCKED');
      }

      // Build updates object for all files to unlock
      const updates = {
        fragmentsRedeemed: currentFragments + 1,
        lastFragmentRedemption: new Date().toISOString()
      };

      filesToUnlock.forEach(file => {
        const trimmedFile = file.trim();
        if (trimmedFile) {
          updates[`unlockedFiles/${trimmedFile}`] = true;
        }
      });

      const response = await fetch(`${apiUrl}${PATHS.PLAYERS}/${playerId}.json`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        throw new Error('Failed to update player data');
      }

      // Log each file unlock
      for (const file of filesToUnlock) {
        await this.logFragmentRedemption(playerId, file.trim(), folderPasswordKey);
      }

      // Return the list of unlocked files
      return filesToUnlock.map(f => f.trim()).filter(f => f);
    } catch (error) {
      console.error('Error redeeming folder password:', error);
      throw error;
    }
  }

}

export default new PlayerService();